import { formatPacketWithByteIndexes } from './PacketFormatter';
import { parseCommandByte } from './ProtocolHeader';
import { isRtMonitorPacket } from './RtMonitor';

/**
 * HID 设备消息处理类
 * 负责处理设备输入报告的接收、解析和分发
 */
class MessageHandler {
  constructor() {
    this.listeners = new Map();
    this.messageBuffer = [];
    this.maxBufferSize = 100;
  }

  /**
   * 注册消息监听器
   * @param {string} eventType - 事件类型
   * @param {Function} callback - 回调函数
   */
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType).push(callback);
  }

  /**
   * 移除消息监听器
   * @param {string} eventType - 事件类型
   * @param {Function} callback - 回调函数
   */
  off(eventType, callback) {
    if (!this.listeners.has(eventType)) return;
    
    const callbacks = this.listeners.get(eventType);
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  /**
   * 触发事件
   * @param {string} eventType - 事件类型
   * @param {*} data - 事件数据
   */
  emit(eventType, data) {
    if (!this.listeners.has(eventType)) return;
    
    this.listeners.get(eventType).forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in ${eventType} listener:`, error);
      }
    });
  }

  /**
   * 安全拷贝 DataView / 类数组为独立 Uint8Array
   * 注意：不能直接用 data.buffer，可能带 byteOffset 或大于实际报告长度
   * @param {DataView|ArrayBuffer|Uint8Array} data
   * @returns {Uint8Array}
   */
  copyReportBytes(data) {
    if (!data) return new Uint8Array(0);
    if (data instanceof Uint8Array) {
      // 独立拷贝，避免后续底层缓冲被复用
      return data.slice();
    }
    if (data instanceof ArrayBuffer) {
      return new Uint8Array(data.slice(0));
    }
    // DataView / TypedArray view：必须按 offset/length 独立拷贝
    if (typeof data.byteLength === 'number' && data.buffer) {
      const offset = data.byteOffset || 0;
      return new Uint8Array(data.buffer.slice(offset, offset + data.byteLength));
    }
    // fallback: DataView.getUint8
    if (typeof data.getUint8 === 'function' && typeof data.byteLength === 'number') {
      const out = new Uint8Array(data.byteLength);
      for (let i = 0; i < data.byteLength; i++) out[i] = data.getUint8(i);
      return out;
    }
    return new Uint8Array(0);
  }

  /**
   * 处理输入报告
   * @param {InputReportEvent} event - HID 输入报告事件
   */
  handleInputReport(event) {
    const { data, device, reportId } = event;
    // 必须按 byteOffset/byteLength 拷贝，避免错误性能/按键 raw 被用来回写
    const response = this.copyReportBytes(data);

    // 记录原始数据。0xF1 监测推送很密，不打印
    if (!isRtMonitorPacket(response)) {
      console.log('Received input report:', {
        reportId,
        length: response.length,
        device: device.productName
      });
      console.log(
        `接收数据（HEX/Byte索引）:\n${formatPacketWithByteIndexes(response)}`
      );
    }

    // 添加到消息缓冲区
    this.addToBuffer({
      timestamp: Date.now(),
      reportId,
      data: response,
      device: device.productName
    });

    // 解析消息
    const parsedMessage = this.parseMessage(response, reportId);
    
    // 触发通用消息事件
    this.emit('message', {
      raw: response,
      parsed: parsedMessage,
      reportId,
      device
    });

    // 根据消息类型触发特定事件
    if (parsedMessage.type) {
      this.emit(parsedMessage.type, parsedMessage);
    }
  }

  /**
   * 解析消息数据
   * @param {Uint8Array} data - 原始数据
   * @param {number} reportId - 报告ID
   * @returns {Object} 解析后的消息
   */
  parseMessage(data, reportId) {
    // 基础消息结构
    const message = {
      reportId,
      type: 'unknown',
      timestamp: Date.now(),
      raw: data,
      header: null,
      command: null,
      deviceType: null,
      protocolType: data?.[3] ?? null,
    };

    // 根据报告ID或数据格式解析具体内容
    if (data.length === 0) {
      return message;
    }

    const header = parseCommandByte(data[0]);
    message.header = header;
    message.command = header.command;
    message.deviceType = header.deviceType;

    // command 必须忽略 byte0 的 deviceType / bit7 位。
    const messageType = header.command;
    
    switch (messageType) {
      case 0x01:
        message.type = 'mouse_input';
        message.data = this.parseMouseInput(data);
        break;
      case 0x02:
        message.type = 'keyboard_input';
        message.data = this.parseKeyboardInput(data);
        break;
      case 0x03:
        message.type = 'config_response';
        message.data = this.parseConfigResponse(data);
        break;
      case 0x04:
        message.type = 'status_update';
        message.data = this.parseStatusUpdate(data);
        break;
      default:
        message.type = 'raw_data';
        message.data = { bytes: Array.from(data) };
    }

    return message;
  }

  /**
   * 解析鼠标输入数据
   * @param {Uint8Array} data - 原始数据
   */
  parseMouseInput(data) {
    return {
      buttons: data[1] || 0,
      x: data[2] || 0,
      y: data[3] || 0,
      wheel: data[4] || 0
    };
  }

  /**
   * 解析键盘输入数据
   * @param {Uint8Array} data - 原始数据
   */
  parseKeyboardInput(data) {
    return {
      modifiers: data[1] || 0,
      keys: Array.from(data.slice(2, 8))
    };
  }

  /**
   * 解析配置响应数据
   * @param {Uint8Array} data - 原始数据
   */
  parseConfigResponse(data) {
    return {
      status: data[1],
      profileId: data[2],
      dpi: (data[3] << 8) | data[4],
      pollingRate: (data[5] << 8) | data[6]
    };
  }

  /**
   * 解析状态更新数据
   * @param {Uint8Array} data - 原始数据
   */
  parseStatusUpdate(data) {
    return {
      batteryLevel: data[1],
      connectionType: data[2],
      currentProfile: data[3]
    };
  }

  /**
   * 添加消息到缓冲区
   * @param {Object} message - 消息对象
   */
  addToBuffer(message) {
    this.messageBuffer.push(message);
    
    // 限制缓冲区大小
    if (this.messageBuffer.length > this.maxBufferSize) {
      this.messageBuffer.shift();
    }
  }

  /**
   * 获取消息历史
   * @param {number} count - 获取数量
   * @returns {Array} 消息数组
   */
  getMessageHistory(count = 10) {
    return this.messageBuffer.slice(-count);
  }

  /**
   * 清空消息缓冲区
   */
  clearBuffer() {
    this.messageBuffer = [];
  }

  /**
   * 转换为十六进制字符串
   * @param {Uint8Array} data - 数据
   * @returns {string} 十六进制字符串
   */
  toHexString(data) {
    return Array.from(data)
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join(' ');
  }

  /**
   * 获取统计信息
   * @returns {Object} 统计数据
   */
  getStats() {
    const typeCount = {};
    this.messageBuffer.forEach(msg => {
      const type = msg.reportId || 'unknown';
      typeCount[type] = (typeCount[type] || 0) + 1;
    });

    return {
      totalMessages: this.messageBuffer.length,
      typeCount,
      bufferSize: this.maxBufferSize
    };
  }

  /**
   * 清理资源
   */
  destroy() {
    this.listeners.clear();
    this.messageBuffer = [];
  }
}

export default MessageHandler;
